import { PageTitle } from "components/PageTitle/PageTitle";
import { useMemo, useState } from "react";
import { candidates } from "routes/SnippetsPage/candidates";
import { categories } from "routes/SnippetsPage/categories";
import { BadgeAuditProposalView } from "routes/SnippetsPage/components/BadgeAuditProposalView";
import { BadgeAuditView } from "routes/SnippetsPage/components/BadgeAuditView";
import { CandidateSection } from "routes/SnippetsPage/components/CandidateSection";
import { SnippetSection } from "routes/SnippetsPage/components/SnippetSection";
import { buildSnippetRegistry } from "routes/SnippetsPage/renderers";

type SnippetTab = "components" | "audit" | "proposal";

/**
 * /snippets — 共通コンポーネントのライブスタイルガイド。
 * ui/ 配下のリーフ、components 直下のコンポジット、
 * まだ共通化されていない候補パターンをまとめて一覧表示する。
 * @return {JSX.Element} ページ全体
 */
const SnippetsPage = (): JSX.Element => {
  const registry = useMemo(buildSnippetRegistry, []);
  const [tab, setTab] = useState<SnippetTab>("components");

  const totalLeaf = registry.filter(s => s.kind === "leaf").length;
  const totalComposite = registry.filter(s => s.kind === "composite").length;

  return (
    <div className="space-y-10 p-6 max-w-5xl">
      <header className="space-y-2">
        <PageTitle>Snippets</PageTitle>
        <p className="text-sm text-base-content/70">
          共通コンポーネントのライブスタイルガイド。各スニペットには
          <code className="mx-1 px-1 bg-base-200 rounded">LEAF-NNNN</code>/
          <code className="mx-1 px-1 bg-base-200 rounded">COMP-NNNN</code>
          のユニーク番号が振られており、UI
          指示の際に参照できる。共通化できていないパターンは「共通化候補」
          セクションにバックログとして列挙している。
        </p>
        <div className="text-xs text-base-content/60 flex gap-4 flex-wrap">
          <span>Leaf: {totalLeaf}</span>
          <span>Composite: {totalComposite}</span>
          <span>Candidates: {candidates.length}</span>
        </div>
      </header>

      {/* タブ */}
      <div className="flex gap-6 border-b border-slate-200">
        {(
          [
            { key: "components", label: "Components" },
            { key: "audit", label: "Status & Tags 現状" },
            { key: "proposal", label: "Status & Tags 理想 (設計原則)" },
          ] as const
        ).map(t => {
          const isActive = tab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={`relative px-2 py-2 text-sm font-medium transition-colors ${
                isActive
                  ? "text-primary"
                  : "text-base-content/60 hover:text-base-content"
              }`}
            >
              {t.label}
              {isActive && (
                <span className="absolute left-0 right-0 -bottom-px h-0.5 bg-primary" />
              )}
            </button>
          );
        })}
      </div>

      {tab === "audit" && <BadgeAuditView />}
      {tab === "proposal" && <BadgeAuditProposalView />}
      {tab === "components" && <ComponentsTabContent registry={registry} />}
    </div>
  );
};

type ComponentsTabContentProps = {
  registry: ReturnType<typeof buildSnippetRegistry>;
};

/**
 * 既存の Components タブの中身。レジストリ + 共通化候補。
 * @param {ComponentsTabContentProps} props registry を受け取る
 * @return {JSX.Element} 既存タブ内容
 */
const ComponentsTabContent = ({
  registry,
}: ComponentsTabContentProps): JSX.Element => {
  return (
    <div className="space-y-10">
      <nav
        aria-label="目次"
        className="border border-base-300 rounded-lg p-4 bg-base-100"
      >
        <p className="text-sm font-bold mb-2">目次</p>
        <ul className="text-sm grid grid-cols-2 gap-y-1 gap-x-4">
          {categories.map(c => (
            <li key={c.key}>
              <a
                href={`#section-${c.key}`}
                className="text-primary hover:underline"
              >
                {c.label}
              </a>
            </li>
          ))}
          <li>
            <a
              href="#section-candidate"
              className="text-primary hover:underline"
            >
              共通化候補
            </a>
          </li>
        </ul>
      </nav>

      {categories.map(category => {
        const snippets = registry.filter(s => s.category === category.key);
        return (
          <SnippetSection
            key={category.key}
            category={category}
            snippets={snippets}
          />
        );
      })}

      <CandidateSection />

      <footer className="text-xs text-base-content/50 border-t border-base-300 pt-4">
        使用箇所の一覧は
        <code className="mx-1 px-1 bg-base-200 rounded">
          node scripts/generate-snippet-usage.js
        </code>
        で再生成できます。新しいコンポーネントを追加する際は
        <code className="mx-1 px-1 bg-base-200 rounded">
          src/routes/SnippetsPage/registry.ts
        </code>
        と
        <code className="mx-1 px-1 bg-base-200 rounded">
          src/routes/SnippetsPage/renderers.tsx
        </code>
        に登録してください。
      </footer>
    </div>
  );
};

export default SnippetsPage;
